
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import Footer from '@/components/Footer';
import NotLoggedInLayout from '@/components/layouts/NotLoggedInLayout';
import { useAuth } from '@/context/AuthContext';
import { Ticket, Zap, Award } from 'lucide-react';
import CookieConsentBanner from '@/components/CookieConsentBanner';
import { getCookie } from '@/utils/cookieUtils';

const Home: React.FC = () => {
  const { t } = useTranslation(['app', 'auth', 'features', 'events', 'projects', 'navigation', 'footer']);
  const navigate = useNavigate();
  const { user } = useAuth();
  const [showCookieConsent, setShowCookieConsent] = useState(false);
  
  useEffect(() => {
    // Check if user has already given cookie consent
    const hasConsent = getCookie('cookie-consent');
    if (!hasConsent) {
      setShowCookieConsent(true);
    }
  }, []);

  return (
    <NotLoggedInLayout>
      <div className="flex flex-col min-h-screen bg-slate-50">
        {/* Hero Section */}
        <div className="bg-gradient-to-b from-[#E4002B] to-[#C7001E] text-white py-16 md:py-24">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                {t('app:welcomeMessage')}
              </h1>
              <p className="text-xl md:text-2xl mb-10 opacity-90">
                {t('app:welcomeSubtitle')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                {user ? (
                  <Button size="lg" onClick={() => navigate('/dashboard')} className="bg-white text-[#E4002B] hover:bg-gray-100">
                    {t('navigation:dashboard')}
                  </Button>
                ) : (
                  <>
                    <Button size="lg" onClick={() => navigate('/login')} className="bg-white text-[#E4002B] hover:bg-gray-100">
                      {t('auth:signIn')}
                    </Button>
                    <Button size="lg" variant="outline" onClick={() => navigate('/events')} className="border-white text-inherit bg-inherit">
                      {t('events:exploreEvents')}
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Features Section */}
        <div className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900">
                {t('footer:featuresTitle')}
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              <div className="p-6 border rounded-lg shadow-sm bg-white text-center">
                <div className="w-12 h-12 bg-[#E4002B]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Zap className="text-[#E4002B] w-6 h-6" />
                </div>
                <h3 className="text-xl font-semibold mb-3">{t('features:earnPoints')}</h3>
                <p className="text-gray-600">{t('features:earnPointsDesc')}</p>
              </div>
              <div className="p-6 border rounded-lg shadow-sm bg-white text-center">
                <div className="w-12 h-12 bg-[#E4002B]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Award className="text-[#E4002B] w-6 h-6" />
                </div>
                <h3 className="text-xl font-semibold mb-3">{t('features:getRewards')}</h3>
                <p className="text-gray-600">{t('features:getRewardsDesc')}</p>
              </div>
              <div className="p-6 border rounded-lg shadow-sm bg-white text-center">
                <div className="w-12 h-12 bg-[#E4002B]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Ticket className="text-[#E4002B] w-6 h-6" />
                </div>
                <h3 className="text-xl font-semibold mb-3">{t('features:exclusiveBenefits')}</h3>
                <p className="text-gray-600">{t('features:exclusiveBenefitsDesc')}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Join Section */}
        <div className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                {t('app:joinToday')}
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                {t('app:joinTodayDesc')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" onClick={() => navigate('/login?signup=true')} className="bg-[#E4002B] hover:bg-[#C7001E]">
                  {t('auth:signUp')}
                </Button>
                <Button size="lg" variant="outline" onClick={() => navigate('/partner')} className="border-[#E4002B] text-[#E4002B] hover:bg-[#E4002B]/10">
                  {t('projects:partnerWithUs')}
                </Button>
              </div>
              <p className="mt-6 text-sm text-gray-500">
                {t('projects:partnerWithUsShortDesc')}
              </p>
              <Button variant="link" className="text-[#E4002B] mt-2" onClick={() => navigate('/partner')}>
                {t('projects:learnMoreAboutPartnering')}
              </Button>
            </div>
          </div>
        </div>

        <Footer />
        
        {/* Cookie Consent Banner */}
        {showCookieConsent && <CookieConsentBanner onClose={() => setShowCookieConsent(false)} />}
      </div>
    </NotLoggedInLayout>
  );
};

export default Home;
